import { t } from '@lingui/core/macro'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import {
  EMPTY_FAVORITES_SNAPSHOT,
  getDocumentFavoritesRepository,
} from 'features/knowledgeDocumentViewer/utils/documentFavoritesRepository'
import { useGeckoNotifications } from 'features/notifications'
import { useGeckoPermissions } from 'features/permissions'
import { useCallback, useSyncExternalStore } from 'react'
import useSWR from 'swr'

const NOOP_SUBSCRIBE = () => () => {}
const EMPTY_SNAPSHOT = () => EMPTY_FAVORITES_SNAPSHOT

export function useDocumentFavoritesRepository(siteId?: string) {
  const { user } = useGeckoPermissions()
  const { orgID } = useKnowledgeDocumentDataContext()
  const { ROLODEX_URL } = useRuntimeVariables()
  const { authorizedAxios, isAuthenticated } = useGeckoAuth()
  const { errorNotification } = useGeckoNotifications()
  const userId = user?.id
  const key =
    isAuthenticated && userId && siteId && orgID
      ? JSON.stringify([
          ROLODEX_URL,
          'document-favorites',
          userId,
          siteId,
          orgID,
        ])
      : undefined

  const repository =
    key && userId && siteId
      ? getDocumentFavoritesRepository(key, {
          userId,
          siteId,
          orgId: orgID,
          rolodexUrl: ROLODEX_URL,
          authorizedAxios,
        })
      : undefined

  const onError = useCallback(() => {
    errorNotification(
      t`Could not load or import document favorites. Please try again.`
    )
  }, [errorNotification])
  const refresh = useCallback(async () => {
    if (!repository) return undefined
    await repository.refresh()
    return repository.getSnapshot()
  }, [repository])
  useSWR(key ?? null, refresh, { onError })

  const snapshot = useSyncExternalStore(
    repository?.subscribe ?? NOOP_SUBSCRIBE,
    repository?.getSnapshot ?? EMPTY_SNAPSHOT
  )
  return { repository, ...snapshot }
}
